// ═══════════════════════════════════════════════════════════════
// JobLineItemsEditor — controlled component that owns the parts +
// labor breakdown on a job. Used by both the JobModal (Jobs.jsx) and
// the JobSidebar (Calendar.jsx) so the editing UX is identical.
// ═══════════════════════════════════════════════════════════════
import React, { useMemo, useState } from 'react';
import { Plus, Trash2, Wrench, Package, Search, Percent, X } from 'lucide-react';
import {
  blankLaborItem, blankPartItem, lineItemAmount,
  computeLaborSubtotal, computePartsSubtotal, computeJobTotal,
  computeJobTax, computeGrandTotal,
  formatPrice,
} from './lib/job-helpers.js';
import { resolvePartSearchLinks } from './lib/part-search-links.js';
import { formatRate } from './lib/tax-presets.js';
import PartSearchPicker, { partSearchQueryFromText } from './PartSearchPicker.jsx';
import TaxPicker from './TaxPicker.jsx';

export default function JobLineItemsEditor({
  lineItems = [],
  shopSupplies = 0,
  laborPresets = [],
  taxPresets = [],
  tax = null,
  partSearchLinks,
  vehicleLabel = '',
  onLineItemsChange,
  onShopSuppliesChange,
  onTaxChange,
  onSaveTaxPreset,
}) {
  const [partLookupQuery, setPartLookupQuery] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [taxPickerOpen, setTaxPickerOpen] = useState(false);

  const searchLinks = useMemo(
    () => resolvePartSearchLinks(partSearchLinks),
    [partSearchLinks],
  );

  const labors = lineItems.filter((li) => li.type === 'labor');
  const parts  = lineItems.filter((li) => li.type === 'part');

  function patchItem(id, patch) {
    onLineItemsChange(
      lineItems.map((li) => (li.id === id ? { ...li, ...patch } : li))
    );
  }
  function replaceItem(id, nextItem) {
    onLineItemsChange(
      lineItems.map((li) => (li.id === id ? nextItem : li))
    );
  }
  function removeItem(id) {
    onLineItemsChange(lineItems.filter((li) => li.id !== id));
  }
  function addLabor() {
    onLineItemsChange([...lineItems, blankLaborItem()]);
  }
  function addPart() {
    onLineItemsChange([...lineItems, blankPartItem()]);
  }
  function applyPreset(preset) {
    if (!preset) return;
    const amount = Number(preset.amount);
    onLineItemsChange([
      ...lineItems,
      {
        ...blankLaborItem(),
        description: preset.description || '',
        amount: Number.isFinite(amount) ? amount : 0,
        sourcePresetId: preset.id || '',
      },
    ]);
  }

  function isHourlyLabor(li) {
    return li?.hours != null || li?.hourlyRate != null;
  }

  function setLaborMode(li, mode) {
    if (mode === 'hourly') {
      replaceItem(li.id, {
        id: li.id,
        type: 'labor',
        description: li.description || '',
        sourcePresetId: li.sourcePresetId || '',
        hours: li.hours ?? 1,
        hourlyRate: li.hourlyRate ?? li.amount ?? 0,
      });
    } else {
      replaceItem(li.id, {
        id: li.id,
        type: 'labor',
        description: li.description || '',
        sourcePresetId: li.sourcePresetId || '',
        amount: lineItemAmount(li),
      });
    }
  }

  function openPicker(rawQuery) {
    const q = partSearchQueryFromText(rawQuery);
    if (!q) return;
    setPickerQuery(q);
    setPickerOpen(true);
  }

  function handleLookupKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      openPicker(partLookupQuery);
    }
  }

  const presets = Array.isArray(laborPresets) ? laborPresets.filter((p) => p?.description) : [];

  const laborSubtotal = computeLaborSubtotal(lineItems);
  const partsSubtotal = computePartsSubtotal(lineItems);
  const supplies = Number(shopSupplies) || 0;
  const total = computeJobTotal({ lineItems, shopSupplies });
  const taxBits = computeJobTax({ lineItems, shopSupplies, tax });
  const taxable = !!tax && (taxBits.partsRate > 0 || taxBits.laborRate > 0);
  const grandTotal = computeGrandTotal({ lineItems, shopSupplies, tax });

  return (
    <div className="line-items-editor">
      <div className="li-section">
        <div className="li-section-head">
          <span className="li-section-title">
            <Wrench size={14} /> Labor
          </span>
          <button type="button" className="btn-link" onClick={addLabor}>
            <Plus size={13} /> Add labor
          </button>
        </div>
        {presets.length > 0 && (
          <div className="li-presets">
            <span className="muted small">Presets:</span>
            {presets.map((p) => (
              <button
                key={p.id || p.description}
                type="button"
                className="li-preset-chip"
                onClick={() => applyPreset(p)}
              >
                {p.description} · {formatPrice(p.amount)}
              </button>
            ))}
          </div>
        )}
        {labors.length === 0 ? (
          <p className="li-empty muted small">No labor lines yet.</p>
        ) : (
          <ul className="li-list">
            {labors.map((li) => {
              const hourly = isHourlyLabor(li);
              return (
                <li key={li.id} className="li-row-block">
                  <div
                    className="li-row"
                    style={{
                      gridTemplateColumns: '86px 1fr 32px',
                      gridTemplateRows: hourly
                        ? 'auto auto auto auto'
                        : 'auto auto auto auto',
                    }}
                  >
                    <input
                      type="text" maxLength={120}
                      className="li-desc"
                      style={{ gridColumn: '1 / -1', gridRow: 1 }}
                      placeholder="What you did (e.g., Fuel pump installation)"
                      value={li.description}
                      onChange={(e) => patchItem(li.id, { description: e.target.value })}
                    />
                    <select
                      className="li-qty"
                      aria-label="Labor pricing mode"
                      style={{ gridColumn: 1, gridRow: 2 }}
                      value={hourly ? 'hourly' : 'flat'}
                      onChange={(e) => setLaborMode(li, e.target.value)}
                    >
                      <option value="flat">Flat</option>
                      <option value="hourly">Hourly</option>
                    </select>
                    {hourly ? (
                      <>
                        <input
                          type="number" min={0} step="0.25" inputMode="decimal"
                          className="li-qty"
                          aria-label="Labor hours"
                          style={{ gridColumn: 1, gridRow: 3 }}
                          placeholder="Hrs"
                          value={li.hours === 0 ? '' : (li.hours ?? '')}
                          onChange={(e) => patchItem(li.id, { hours: Number(e.target.value) || 0 })}
                        />
                        <div className="li-money" style={{ gridColumn: '2 / -1', gridRow: 3 }}>
                          <span className="li-money-prefix">$</span>
                          <input
                            type="number" min={0} step="0.01" inputMode="decimal"
                            className="li-amount"
                            aria-label="Hourly rate"
                            placeholder="/hr"
                            value={li.hourlyRate === 0 ? '' : (li.hourlyRate ?? '')}
                            onChange={(e) => patchItem(li.id, { hourlyRate: Number(e.target.value) || 0 })}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="li-money" style={{ gridColumn: '1 / -1', gridRow: 3 }}>
                        <span className="li-money-prefix">$</span>
                        <input
                          type="number" min={0} step="0.01" inputMode="decimal"
                          className="li-amount"
                          aria-label="Flat labor amount"
                          placeholder="0.00"
                          value={li.amount === 0 ? '' : (li.amount ?? '')}
                          onChange={(e) => patchItem(li.id, { amount: Number(e.target.value) || 0 })}
                        />
                      </div>
                    )}
                    <span
                      className="li-row-total mono"
                      style={{ gridColumn: '1 / -1', gridRow: 4, justifySelf: 'end' }}
                    >
                      {formatPrice(lineItemAmount(li))}
                    </span>
                    <button
                      type="button"
                      className="li-remove"
                      style={{
                        gridColumn: 3,
                        gridRow: 2,
                      }}
                      onClick={() => removeItem(li.id)}
                      aria-label="Remove labor line"
                      title="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {labors.length > 0 && (
          <div className="li-subtotal">
            <span className="muted small">Labor subtotal</span>
            <span className="mono">{formatPrice(laborSubtotal)}</span>
          </div>
        )}
      </div>

      <div className="li-section">
        <div className="li-section-head">
          <span className="li-section-title">
            <Package size={14} /> Parts
          </span>
          <button type="button" className="btn-link" onClick={addPart}>
            <Plus size={13} /> Add part
          </button>
        </div>

        {searchLinks.length > 0 && (
          <div className="part-lookup">
            <label className="part-lookup-label muted small" htmlFor="part-lookup-input">
              Look up part prices
            </label>
            <div className="part-lookup-row">
              <input
                id="part-lookup-input"
                type="text"
                className="part-lookup-input"
                maxLength={120}
                placeholder="Part name or number (e.g., brake pads)"
                value={partLookupQuery}
                onChange={(e) => setPartLookupQuery(e.target.value)}
                onKeyDown={handleLookupKeyDown}
              />
              <button
                type="button"
                className="btn part-lookup-go"
                disabled={!partLookupQuery.trim()}
                onClick={() => openPicker(partLookupQuery)}
              >
                <Search size={16} /> Search
              </button>
            </div>
            <p className="muted small part-lookup-hint">Enter or tap Search to pick a store — or open all at once.</p>
          </div>
        )}

        {parts.length === 0 ? (
          <p className="li-empty muted small">No parts on this job.</p>
        ) : (
          <ul className="li-list">
            {parts.map((li) => {
              const lineTotal = lineItemAmount(li);
              return (
                <li key={li.id} className="li-row-block">
                  <div className="li-row li-row-part">
                    <input
                      type="text" maxLength={120}
                      className="li-desc"
                      placeholder="Part (e.g., Fuel pump, NAPA 12345)"
                      value={li.description}
                      onChange={(e) => patchItem(li.id, { description: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          openPicker(li.description);
                        }
                      }}
                    />
                    <input
                      type="number" min={1} step={1} inputMode="numeric"
                      className="li-qty"
                      placeholder="Qty"
                      value={li.qty}
                      onChange={(e) => patchItem(li.id, { qty: Number(e.target.value) || 0 })}
                    />
                    <div className="li-money">
                      <span className="li-money-prefix">$</span>
                      <input
                        type="number" min={0} step="0.01" inputMode="decimal"
                        className="li-amount"
                        placeholder="0.00"
                        value={li.unitPrice === 0 ? '' : li.unitPrice}
                        onChange={(e) => patchItem(li.id, { unitPrice: Number(e.target.value) || 0 })}
                      />
                    </div>
                    <span className="li-row-total mono">{formatPrice(lineTotal)}</span>
                    {searchLinks.length > 0 && (
                      <button
                        type="button"
                        className="li-part-search-btn"
                        title="Search store prices"
                        aria-label="Search store prices for this part"
                        disabled={!li.description.trim()}
                        onClick={() => openPicker(li.description)}
                      >
                        <Search size={14} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="li-remove"
                      onClick={() => removeItem(li.id)}
                      aria-label="Remove part line"
                      title="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {parts.length > 0 && (
          <div className="li-subtotal">
            <span className="muted small">Parts subtotal</span>
            <span className="mono">{formatPrice(partsSubtotal)}</span>
          </div>
        )}
      </div>

      <div className="li-section li-section-supplies">
        <label className="li-supplies-row">
          <span className="li-section-title">Shop supplies</span>
          <div className="li-money">
            <span className="li-money-prefix">$</span>
            <input
              type="number" min={0} step="0.01" inputMode="decimal"
              className="li-amount"
              placeholder="0.00"
              value={supplies === 0 ? '' : supplies}
              onChange={(e) => onShopSuppliesChange(Number(e.target.value) || 0)}
            />
          </div>
        </label>
        <p className="muted small li-supplies-hint">
          Optional flat amount for rags, fluids, fasteners, etc.
        </p>
      </div>

      <div className="li-section li-section-tax">
        <div className="li-section-head">
          <span className="li-section-title">
            <Percent size={14} /> Sales tax
          </span>
          {taxable ? (
            <button
              type="button"
              className="btn-link"
              onClick={() => setTaxPickerOpen(true)}
            >
              Change
            </button>
          ) : (
            <button
              type="button"
              className="btn-link"
              onClick={() => setTaxPickerOpen(true)}
            >
              <Plus size={13} /> Add tax
            </button>
          )}
        </div>

        {!taxable ? (
          <p className="li-empty muted small">
            No tax on this job.
          </p>
        ) : (
          <>
            {tax?.label && (
              <div className="li-tax-label-row">
                <span className="li-tax-chip">{tax.label}</span>
                <button
                  type="button"
                  className="li-remove"
                  onClick={() => onTaxChange(null)}
                  aria-label="Remove tax from this job"
                  title="No tax on this job"
                >
                  <X size={14} />
                </button>
              </div>
            )}
            <ul className="li-list li-tax-list">
              {taxBits.laborTax > 0 && (
                <li className="li-tax-row">
                  <span className="muted small">
                    Labor tax · {formatRate(taxBits.laborRate)} of {formatPrice(taxBits.laborBase)}
                  </span>
                  <span className="mono">{formatPrice(taxBits.laborTax)}</span>
                </li>
              )}
              {taxBits.partsAndSuppliesTax > 0 && (
                <li className="li-tax-row">
                  <span className="muted small">
                    Parts tax · {formatRate(taxBits.partsRate)} of{' '}
                    {formatPrice(taxBits.partsBase + taxBits.suppliesBase)}
                    {taxBits.suppliesBase > 0 ? ' (incl. supplies)' : ''}
                  </span>
                  <span className="mono">{formatPrice(taxBits.partsAndSuppliesTax)}</span>
                </li>
              )}
            </ul>
            {!tax?.label && (
              <div className="li-tax-label-row">
                <button
                  type="button"
                  className="btn-link danger"
                  onClick={() => onTaxChange(null)}
                >
                  Remove tax
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {taxable ? (
        <div className="li-grand-total li-grand-total-taxed">
          <div className="li-grand-sub">
            <span className="muted small">Subtotal</span>
            <span className="mono">{formatPrice(total)}</span>
          </div>
          <div className="li-grand-sub">
            <span className="muted small">Tax</span>
            <span className="mono">{formatPrice(taxBits.total)}</span>
          </div>
          <div className="li-grand-main">
            <span className="li-grand-label">Total</span>
            <span className="li-grand-amount mono">{formatPrice(grandTotal)}</span>
          </div>
        </div>
      ) : (
        <div className="li-grand-total">
          <span className="li-grand-label">Total</span>
          <span className="li-grand-amount mono">{formatPrice(total)}</span>
        </div>
      )}

      <PartSearchPicker
        open={pickerOpen}
        query={pickerQuery}
        vehicleLabel={vehicleLabel}
        partSearchLinks={partSearchLinks}
        onClose={() => setPickerOpen(false)}
      />

      <TaxPicker
        open={taxPickerOpen}
        presets={taxPresets}
        lineItems={lineItems}
        shopSupplies={shopSupplies}
        current={tax}
        onApply={onTaxChange}
        onSavePreset={onSaveTaxPreset}
        onClose={() => setTaxPickerOpen(false)}
      />
    </div>
  );
}
